import React from 'react'
import { cn } from '@/lib/utils'
import { buildMonthWeeks } from '@/lib/calendar-utils'
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip'
import CalendarDayPopup from './CalendarDayPopup'
import { formatPickupRooms } from '@/lib/plain-language'
import type { OccupancyDay, RateData, RoomType } from '@/types/calendar'

interface OccupancyCalendarGridProps {
  year: number
  month: number
  occupancyByDate: Record<string, OccupancyDay>
  propertyOccupancyByDate?: Record<string, { booked: number; available: number; occupancyPct: number; label?: string | null }>
  currency?: string
  rateByDate?: Record<string, RateData>
  roomType?: RoomType
  onCellClick?: (date: string) => void
  adrData?: Record<string, { adr: number | null; lastYearAdr: number | null }>
  /** Today AT THE PROPERTY (YYYY-MM-DD) — see calendar-utils#todayIso. */
  today: string
}

const DAY_HEADERS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun']

function occupancyBg(pct: number): string {
  if (pct >= 80) return 'bg-emerald-50 border-emerald-200'
  if (pct >= 60) return 'bg-amber-50 border-amber-200'
  return 'bg-white border-border/60'
}

function occupancyTextColor(pct: number): string {
  if (pct >= 80) return 'text-emerald-700'
  if (pct >= 60) return 'text-amber-700'
  return 'text-foreground'
}

function OccupancyCalendarGrid({
  year,
  month,
  occupancyByDate,
  propertyOccupancyByDate,
  currency,
  rateByDate,
  roomType,
  onCellClick,
  adrData,
  today: propertyToday,
}: OccupancyCalendarGridProps) {
  const weeks = buildMonthWeeks(year, month)

  return (
    <TooltipProvider delayDuration={800} skipDelayDuration={0}>
      <div className="rounded-xl border border-border/60 bg-white shadow-sm overflow-hidden">
        {/* Day headers */}
        <div className="grid grid-cols-7 border-b border-border/40">
          {DAY_HEADERS.map((d, i) => (
            <div
              key={d}
              className={cn(
                'px-2 py-2.5 text-center text-xs font-semibold uppercase tracking-wider',
                i >= 5 ? 'text-primary/70 bg-slate-50/50' : 'text-muted-foreground'
              )}
            >
              {d}
            </div>
          ))}
        </div>

        {/* Weeks */}
        {weeks.map((week, wi) => (
          <div key={wi} className="grid grid-cols-7 border-b border-border/30 last:border-b-0">
            {week.map((dateStr, di) => {
              if (!dateStr) {
                return <div key={`empty-${di}`} className="min-h-[100px] bg-slate-50/30 border-r border-border/20 last:border-r-0" />
              }

              const occ = occupancyByDate[dateStr]
              const propertyOcc = propertyOccupancyByDate?.[dateStr]
              const showHotel = propertyOcc && (!occ || propertyOcc.available !== occ.available)
              const today = dateStr === propertyToday
              const isPast = dateStr < propertyToday
              const weekend = di >= 5
              const dayNum = new Date(dateStr + 'T00:00:00').getDate()
              const dayOfWeek = DAY_HEADERS[di]

              return (
                <div
                  key={dateStr}
                  className={cn(
                    'min-h-[100px] border-r border-border/20 last:border-r-0 p-0',
                    isPast && 'opacity-40',
                    occ && !isPast ? occupancyBg(occ.occupancyPct) : '',
                    today && 'ring-2 ring-inset ring-primary/30',
                    weekend && !today && !isPast && !occ && 'bg-slate-50/30'
                  )}
                >
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <div
                        onClick={() => onCellClick?.(dateStr)}
                        className="min-h-[100px] p-2 transition-colors cursor-pointer hover:brightness-95"
                      >
                        <div className="flex items-center justify-between mb-1.5">
                          <span className={cn(
                            'text-xs font-bold',
                            today ? 'text-primary' : 'text-foreground'
                          )}>
                            {dayNum}
                          </span>
                        </div>

                        {occ && (
                          <div className="space-y-0.5">
                            <div className={cn('text-base font-bold tabular-nums leading-tight', occupancyTextColor(occ.occupancyPct))}>
                              {occ.occupancyPct}%
                            </div>
                            <div className="text-[10px] text-muted-foreground tabular-nums">
                              {occ.booked}/{occ.available}
                              {showHotel ? (
                                <>
                                  <span className="mx-1 text-muted-foreground/50">·</span>
                                  <span className={cn(
                                    'font-medium',
                                    propertyOcc!.occupancyPct >= 80 ? 'text-emerald-600' :
                                    propertyOcc!.occupancyPct >= 60 ? 'text-amber-600' : 'text-slate-500'
                                  )}>
                                    {propertyOcc!.occupancyPct}%
                                  </span>
                                  {/* "building" at a combined inventory (server label), "hotel" elsewhere. */}
                                  <span className={cn('ml-1 text-muted-foreground/70', propertyOcc!.label && 'lowercase')}>
                                    {propertyOcc!.label ?? 'hotel'}
                                  </span>
                                </>
                              ) : ' rooms'}
                            </div>
                            {occ.pickup7dRooms != null && occ.pickup7dRooms !== 0 && (
                              <div className={cn(
                                'text-[10px] font-medium tabular-nums',
                                occ.pickup7dRooms > 0 ? 'text-emerald-600' : 'text-rose-600'
                              )}>
                                {formatPickupRooms(occ.pickup7dRooms)} this week
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                    </TooltipTrigger>
                    <TooltipContent
                      side="bottom"
                      sideOffset={4}
                      className="max-w-none overflow-visible border-none bg-transparent p-0 shadow-none"
                    >
                      <CalendarDayPopup
                        today={propertyToday}
                        date={dateStr}
                        dayOfWeek={dayOfWeek}
                        currency={currency || 'GBP'}
                        rate={rateByDate?.[dateStr]}
                        occupancy={occ ? {
                          booked: occ.booked,
                          available: occ.available,
                          occupancyPct: occ.occupancyPct,
                          pickup7d: occ.pickup7d,
                          pickup7dRooms: occ.pickup7dRooms,
                        } : undefined}
                        propertyOccupancy={propertyOccupancyByDate?.[dateStr]}
                        roomType={roomType}
                        adr={adrData?.[dateStr]?.adr}
                        lastYearAdr={adrData?.[dateStr]?.lastYearAdr}
                      />
                    </TooltipContent>
                  </Tooltip>
                </div>
              )
            })}
          </div>
        ))}
      </div>
    </TooltipProvider>
  )
}

export default React.memo(OccupancyCalendarGrid)
